import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import type {
  ActivationView,
  BusinessDetails,
  OnboardingStatus,
} from "@/lib/api/onboarding-activation"
import { ActivationLayout, type StepKey } from "./activation-chrome"
import { WelcomeStep } from "./welcome-step"
import { ShopStep } from "./shop-step"
import { SystemsStep } from "./systems-step"
import { ItContactStep } from "./it-contact-step"
import { SignStep } from "./sign-step"
import { SignedStep } from "./signed-step"
import { BookingStep } from "./booking-step"
import { DoneStep } from "./done-step"

// Mirrors CCi's Zoho onboarding form, minus payment: shop info → systems →
// IT contact → Data Authorization signature → (signed celebration) → book
// the installation call. "signed" is transient — shown right after signing
// in-session; a refresh resumes at booking.
type Phase = "welcome" | "shop" | "systems" | "contact" | "sign" | "signed" | "book" | "done"

// Resume from server status — the session is the source of truth. The three
// intake steps save as ONE server call at the end of the IT-contact step, so
// a refresh resumes either at the start of the intake or at the signature.
function initialPhase(status: OnboardingStatus): Phase {
  switch (status) {
    case "details_completed":
      return "sign"
    case "agreement_signed":
      return "book"
    case "call_booked":
      return "done"
    default:
      return "welcome"
  }
}

const PANEL_STEP: Record<Phase, StepKey> = {
  welcome: "shop",
  shop: "shop",
  systems: "systems",
  contact: "contact",
  sign: "sign",
  signed: "book",
  book: "book",
  done: "book",
}

export function ActivationFlow({
  token,
  initial,
}: {
  token: string
  initial: ActivationView
}) {
  const [data, setData] = useState<ActivationView>(initial)
  const [phase, setPhase] = useState<Phase>(initialPhase(initial.status))
  // The intake draft — edited across the three detail steps, saved once.
  const [draft, setDraft] = useState<BusinessDetails>(initial.businessDetails)

  const patchDraft = (patch: BusinessDetails) => setDraft((prev) => ({ ...prev, ...patch }))

  // Non-tech-savvy users land mid-scroll otherwise — every step starts at the top.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior })
  }, [phase])

  // Sidebar navigation — jump back to any already-visited step (the mock
  // allows re-saving details and re-signing; booking is the only terminal
  // action). Forward jumps are never offered by the sidebar.
  const editable: Phase[] = ["shop", "systems", "contact", "sign", "book"]
  const stepClick = editable.includes(phase)
    ? (key: StepKey) => setPhase(key as Phase)
    : undefined

  return (
    <ActivationLayout
      data={data}
      current={PANEL_STEP[phase]}
      welcome={phase === "welcome"}
      onStepClick={stepClick}
    >
      <AnimatePresence
        mode="wait"
        onExitComplete={() => window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior })}
      >
        <motion.div
          key={phase}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ type: "spring", stiffness: 300, damping: 32, opacity: { duration: 0.22 } }}
        >
          {phase === "welcome" && (
            <WelcomeStep data={data} onStart={() => setPhase("shop")} />
          )}
          {phase === "shop" && (
            <ShopStep
              draft={draft}
              onBack={() => setPhase("welcome")}
              onNext={(patch) => {
                patchDraft(patch)
                setPhase("systems")
              }}
            />
          )}
          {phase === "systems" && (
            <SystemsStep
              draft={draft}
              onBack={() => setPhase("shop")}
              onNext={(patch) => {
                patchDraft(patch)
                setPhase("contact")
              }}
            />
          )}
          {phase === "contact" && (
            <ItContactStep
              token={token}
              draft={draft}
              onBack={() => setPhase("systems")}
              onSaved={(next) => {
                setData(next)
                setDraft(next.businessDetails)
                setPhase("sign")
              }}
            />
          )}
          {phase === "sign" && (
            <SignStep
              token={token}
              data={data}
              onBack={() => setPhase("contact")}
              onSigned={(next) => {
                setData(next)
                setPhase("signed")
              }}
            />
          )}
          {phase === "signed" && (
            <SignedStep data={data} onContinue={() => setPhase("book")} />
          )}
          {phase === "book" && (
            <BookingStep
              token={token}
              data={data}
              onBack={() => setPhase("sign")}
              onBooked={(next) => {
                setData(next)
                setPhase("done")
              }}
            />
          )}
          {phase === "done" && <DoneStep token={token} data={data} />}
        </motion.div>
      </AnimatePresence>
    </ActivationLayout>
  )
}
